import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner, Alert, Table, Pagination, PaginationItem, PaginationLink } from '../../../shim/reactstrap';
import apiClient from '../../../api/ApiClient';

/**
 * Unified differences dialog used by both the bulk Models Analysis page and
 * the per-package-ticker analysis page for both standard (daily-candle)
 * tickers and custom (special) tables.
 *
 * The two backend endpoints now share the same row-vs-row comparison code,
 * so the modal just dispatches on `kind`:
 *   - kind="candle" — calls /api/models/candle-differences
 *   - kind="custom" — calls /api/models/custom-table-differences (default)
 *
 * The response payload is the same shape in both cases:
 *   { Items, TotalCount, TotalPages, Columns?, Gaps? }
 *
 * `Columns` and `Gaps` are only populated by the custom-table endpoint today
 * but the modal renders them whenever present so it stays forward-compatible.
 */
export const DataSourceDifferencesModal = ({
  isOpen,
  toggle,
  kind = 'custom',
  subfolder,
  tickerCode,
  tickerId,
  tickerName,
  intersectionStart,
  intersectionEnd,
  tolerance = 0.005,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const fetchDifferences = useCallback(async (page) => {
    try {
      setLoading(true);
      setError(null);
      let result;
      if (kind === 'candle') {
        result = await apiClient.getCandleDifferences(
          subfolder, tickerCode, tickerId, page, pageSize, tolerance);
      } else {
        result = await apiClient.getCustomTableDifferences(
          subfolder, tickerCode, tickerName, page, pageSize, tolerance);
      }
      setData(result);
      setCurrentPage(page);
    } catch (exc) {
      console.error('Failed to fetch differences:', exc);
      setError(exc.message || 'Failed to fetch differences');
    } finally {
      setLoading(false);
    }
  }, [kind, subfolder, tickerCode, tickerId, tickerName, tolerance]);

  useEffect(() => {
    if (isOpen) {
      fetchDifferences(1);
    }
  }, [isOpen, fetchDifferences]);

  const handlePageChange = (page) => {
    if (page !== currentPage && page >= 1 && data && page <= data.TotalPages) {
      fetchDifferences(page);
    }
  };

  const renderPagination = () => {
    if (!data || data.TotalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(data.TotalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <PaginationItem key="first">
          <PaginationLink first onClick={() => handlePageChange(1)} />
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)} />
      </PaginationItem>
    );

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    pages.push(
      <PaginationItem key="next" disabled={currentPage === data.TotalPages}>
        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)} />
      </PaginationItem>
    );

    if (endPage < data.TotalPages) {
      pages.push(
        <PaginationItem key="last">
          <PaginationLink last onClick={() => handlePageChange(data.TotalPages)} />
        </PaginationItem>
      );
    }

    return <Pagination>{pages}</Pagination>;
  };

  const renderValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">N/A</span>;
    }

    const numeric = Number(value);
    if (!isNaN(numeric) && isFinite(numeric)) {
      return Number(value).toFixed(4).replace(/\.?0+$/, '');
    }

    return value;
  };

  const title = tickerName || tickerCode;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        Row Differences - {title}
      </ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <strong>Folder:</strong> <code>{subfolder}</code>{' '}
          <strong className="ms-3">Ticker:</strong> <code>{tickerCode}</code>
        </div>

        {intersectionStart && intersectionEnd && (
          <div className="mb-3">
            <strong>Intersection Period:</strong> {new Date(intersectionStart).toLocaleDateString()} - {new Date(intersectionEnd).toLocaleDateString()}
          </div>
        )}

        {data?.Columns && data.Columns.length > 0 && (
          <div className="mb-3">
            <strong>Columns:</strong>{' '}
            <span className="text-muted" style={{ fontSize: '0.9em' }}>
              {data.Columns.join(', ')}
            </span>
          </div>
        )}

        {error && (
          <Alert color="danger" fade={false}>
            {error}
          </Alert>
        )}

        {loading && (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <div className="mt-2">Loading differences...</div>
          </div>
        )}

        {!loading && data && (
          <>
            <div className="mb-3">
              <strong>Total Differences:</strong> {data.TotalCount}
              {data.TotalCount > 0 && (
                <span className="ms-2 text-muted">
                  (Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, data.TotalCount)})
                </span>
              )}
            </div>

            {data.Gaps && data.Gaps.length > 0 && (
              <div className="mb-3">
                <strong className="text-danger">Data Gaps in DB Package:</strong>
                <div className="text-muted" style={{ fontSize: '0.9em' }}>
                  The following date ranges are present in the original CSV package but missing from the DB package:
                </div>
                <ul className="mt-2" style={{ fontSize: '0.9em' }}>
                  {data.Gaps.map((gap, gapIdx) => (
                    <li key={gapIdx}>
                      {new Date(gap.Start).toLocaleDateString()} - {new Date(gap.End).toLocaleDateString()}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.Items && data.Items.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table striped bordered size="sm">
                    <thead>
                      <tr>
                        <th style={{ width: '150px' }}>Date</th>
                        <th style={{ width: '120px' }}>Column</th>
                        <th>DB Package Value</th>
                        <th>Original Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.Items.map((diff, idx) => (
                        <tr key={idx}>
                          <td>{new Date(diff.Date).toLocaleDateString()}</td>
                          <td><strong>{diff.Column || diff.Field}</strong></td>
                          <td>{renderValue(diff.DatabaseValue)}</td>
                          <td>{renderValue(diff.OriginalValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-flex justify-content-center mt-3">
                  {renderPagination()}
                </div>
              </>
            ) : (
              <Alert color="info" fade={false}>
                No value differences found.
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};